import { useEffect, useRef } from 'react'
import { AirbridgeEvents } from '@healingpaper/shared'
import { getEnv } from '@unni/platform'

import TopDownloadScreen from '@/components/atomic/templates/TopDownloadScreen'
import ResponsiveLayout from '@/components/atomic/templates/ResponsiveLayout'
import { getServiceOfferById } from '@/client/api/service-offer'
import ContextEnhancer from '@/shared/libs/context-enhancer'
import { ServiceOfferTemplate } from '@/features/service-offer/components/service-offer-template'
import FlexCol from '@/components/shared/FlexCol'
import { Locales } from '@/utils/language'
import { JsonLd } from '@/components'
import { getReviewJsonLd, getServiceOfferJsonLd } from '@/utils/scheme'
import { addCriteoEvent, postAmplitude, postGA } from '@/core/lib'
import { sendAirbridgeEvent } from '@/utils/airbridge'
import { User } from '@/features/member/member.type'
import { getServiceOfferReviewsInternal } from '@/features/review/api/get-hospital-reviews'
import { ReviewsPaginationResponseInternal } from '@/features/review/api/review.type'
import { getDefaultMeta, SEOMeta } from '@/shared/utils'
import { type UserServiceOfferDetails } from '@/features/service-offer/service-offer.type'

import type { OptionalAgreeType } from '@/components/atomic/organisms/NormalConsultationBottomSheet'
import type { GetServerSideProps, GetServerSidePropsResult } from 'next'

interface Props {
  serviceOffer: UserServiceOfferDetails
  optionId: string | null
  user: User
  reviewWithPagination: ReviewsPaginationResponseInternal | null
  meta: SEOMeta
  optionalMarketingItems?: (keyof OptionalAgreeType)[]
  error?: Error
}

const EventDetailPage = ({ user, serviceOffer, optionId, reviewWithPagination }: Props) => {
  useUserSession({ userId: user.id })
  useScrollToTop()
  useViewEventDetail({ serviceOffer })
  useSendAirbridgeEvent({ serviceOffer, optionId })
  useAddCriteoEvent({ userLoginId: user.loginId, eventId: serviceOffer.id })

  return (
    <ResponsiveLayout>
      <TopDownloadScreen userProfileImage={user.profileImage || undefined} bottomPaddingToAdd="108px">
        <ServiceOfferTemplate serviceOffer={serviceOffer} user={user} reviewWithPagination={reviewWithPagination}>
          <ServiceOfferTemplate.CardImage />
          <FlexCol gap={10} className="bg-gray-200">
            <FlexCol px={20} py={20} gap={16} className="bg-white">
              <ServiceOfferTemplate.MetaInfoWithRating />
              <ServiceOfferTemplate.SelectedOption />
              <ServiceOfferTemplate.Price />
              <ServiceOfferTemplate.PointRewards />
              <ServiceOfferTemplate.SurgicalRewards />
              <ServiceOfferTemplate.ExclusivePrice />
              <ServiceOfferTemplate.EventPeriod />
            </FlexCol>
            <ServiceOfferTemplate.HospitalInfo />
            <ServiceOfferTemplate.Review />
            <FlexCol gap={40} px={20} py={24} className="bg-white">
              <ServiceOfferTemplate.Description />
              <ServiceOfferTemplate.Options />
              <FlexCol gap={40}>
                <ServiceOfferTemplate.RecommendedFor />
                <ServiceOfferTemplate.TreatmentProcess />
                <ServiceOfferTemplate.TreatmentDowntime />
                <ServiceOfferTemplate.SideEffects />
              </FlexCol>
            </FlexCol>
            <ServiceOfferTemplate.DescriptionImages />
            <ServiceOfferTemplate.LocationMap />
            <ServiceOfferTemplate.CTAButtons />
          </FlexCol>
        </ServiceOfferTemplate>
      </TopDownloadScreen>
      <JsonLd
        data={getServiceOfferJsonLd({
          serviceOffer,
          ...(optionId ? { optionId } : {}),
          reviews: reviewWithPagination?.contents.map((review) =>
            getReviewJsonLd({
              aggregateRating: {
                ratingCount: serviceOffer.rating.ratingCount,
                ratingValue: serviceOffer.rating.totalRating,
              },
              itemReviewed: { name: serviceOffer.title },
              reviewBody: review.translation?.description ?? review.description ?? '',
              author: { name: review.author.nickName ?? '' },
              publisher: { name: serviceOffer.hospital.name ?? '' },
            }),
          ),
        })}
      />
    </ResponsiveLayout>
  )
}

const useUserSession = ({ userId }: { userId: number }) => {
  return useEffect(() => {
    if (userId && userId !== 0) {
      sessionStorage.setItem('user_id', userId.toString())
    } else {
      sessionStorage.removeItem('user_id')
    }
  }, [userId])
}

const useViewEventDetail = ({ serviceOffer }: { serviceOffer: UserServiceOfferDetails }) => {
  return useEffect(() => {
    postGA('view_event', 'pageview', `EV_${serviceOffer.id.toString()}`)
    postAmplitude('view_event_detail', {
      event_id: serviceOffer?.id,
      event_title: serviceOffer?.title,
      hospital_id: serviceOffer?.hospital?.id,
      hospital_name: serviceOffer?.hospital?.name || '',
      original_cost: serviceOffer?.price.discountedAmount,
      discounted_cost: serviceOffer?.price.discountedAmount,
      hospital_region: serviceOffer?.hospital?.location?.addresses[0].address,
      hospital_country: serviceOffer?.hospital?.country || '',
    })
  }, [serviceOffer])
}

const useScrollToTop = () => {
  return useEffect(() => {
    const scrollEl = document.querySelector<HTMLDivElement>('#screenPage')
    scrollEl?.scrollTo({ top: 0 })
  }, [])
}

const useSendAirbridgeEvent = ({
  serviceOffer,
  optionId,
}: {
  serviceOffer: UserServiceOfferDetails
  optionId: string | null
}) => {
  return useEffect(() => {
    const representativeOption =
      serviceOffer.options.find((option) => option.id === optionId) || serviceOffer.options[0]
    const semanticAttributes = {
      type: 'EVENT',
      currency: serviceOffer.price.currency,
      products: [
        {
          productID: serviceOffer.id,
          name: serviceOffer.title,
          categoryID: representativeOption.treatments.map((treatment) => treatment.id).join(','),
          categoryName: representativeOption.treatments.map((treatment) => treatment.name).join(','),
          brandID: serviceOffer.hospital.id,
          brandName: serviceOffer.hospital.name,
          price: serviceOffer.price.discountedAmount,
          currency: serviceOffer.price.currency,
        },
      ],
    }
    sendAirbridgeEvent(AirbridgeEvents.ECOMMERCE_PRODUCT_VIEWED, {
      semanticAttributes,
    })
  }, [serviceOffer, optionId])
}

const useAddCriteoEvent = ({ userLoginId, eventId }: { userLoginId: string; eventId: number }) => {
  const hasAddCriteoEventBeenCalled = useRef(false)
  return useEffect(() => {
    if (userLoginId && eventId && !hasAddCriteoEventBeenCalled.current) {
      addCriteoEvent(userLoginId, {
        event: 'viewItem',
        item: eventId.toString(),
      })

      hasAddCriteoEventBeenCalled.current = true
    }
  }, [userLoginId, eventId])
}

export const getServerSideProps: GetServerSideProps = async (
  ctx,
): Promise<GetServerSidePropsResult<Props | { error: Error }>> => {
  const eventId = Number.parseInt(ctx.params?.eventId as string)
  const optionId = (ctx.query.optionId as string) || null
  const contextEnhancer = new ContextEnhancer(ctx)
  const enhancedContext = (await contextEnhancer.activateAuth()).activateLocale().getContext()
  const { user, token, locale, country, pathnamePrefix } = enhancedContext

  const userIp = (ctx.req.headers['x-forwarded-for'] || ctx.req.socket.remoteAddress) as string
  const headers = { 'Accept-Language': locale, Authorization: token || '', 'x-forwarded-for': userIp }

  try {
    if (Number.isNaN(Number(ctx.query.eventId))) {
      throw new Error('event id is not number.')
    }

    const [eventPromise, reviewsPromise] = await Promise.allSettled([
      getServiceOfferById(eventId, headers),
      locale === Locales.JA_JP || user.id !== 0
        ? getServiceOfferReviewsInternal(headers, eventId)
        : Promise.resolve<ReviewsPaginationResponseInternal>({
            contents: [],
            totalPageNumber: 0,
            totalElementNumber: 0,
            currentPageIndex: 0,
            pageSize: 0,
            isLastPage: true,
          }),
    ])

    if (eventPromise.status === 'rejected') {
      throw new Error('service offer request rejected.')
    }
    const event = eventPromise.value

    const reviewWithPagination = reviewsPromise.status === 'fulfilled' ? reviewsPromise.value : null

    if (!event.available) {
      const hospitalId = event.hospital.id
      const redirectPath = `${pathnamePrefix}/hospitals/${hospitalId}?tabindex=2`
      return {
        redirect: {
          destination: redirectPath,
          permanent: false,
        },
      }
    }

    const optionalMarketingItems: (keyof OptionalAgreeType)[] = []
    if (user?.sendPushAd) {
      optionalMarketingItems.push('sendPushAd')
    }
    if (user?.agreeEmail) {
      optionalMarketingItems.push('agreeEmail')
    }
    if (user?.agreeSms) {
      optionalMarketingItems.push('agreeSms')
    }

    const meta = getDefaultMeta(locale, {
      ...getMeta(locale, event),
      canonical: `${getEnv('NEXT_PUBLIC_HOST')}${pathnamePrefix}/events/${event.id}`,
    })

    return {
      props: {
        user,
        serviceOffer: event,
        reviewWithPagination,
        optionId,
        meta,
        optionalMarketingItems,
      },
    }
  } catch (e) {
    ctx.res.setHeader('location', country ? `/${country}/events` : '/events')
    ctx.res.statusCode = 302
    ctx.res.end()

    return {
      props: {
        error: e as Error,
      },
    }
  }
}

export default EventDetailPage

const getMeta = (locale: Locales, serviceOffer: UserServiceOfferDetails): SEOMeta => {
  const descriptionMap: Record<Locales, string> = {
    [Locales.KO_KR]:
      '국내 1위 미용의료앱 강남언니. 리얼후기부터 가격비교, 상담예약까지 한번에. 강남언니를 지금 만나보세요.',
    [Locales.TH_TH]: `ดูรีวิวจริงที่ผ่านการยืนยัน และโปรโมชั่นพิเศษที่พบได้เฉพาะในแอป UNNI | ${serviceOffer.description?.slice(0, 50) || ''}`,
    [Locales.JA_JP]:
      'お得なクーポン数日本No.1！美容医療・整形の口コミ予約アプリ。国内100万ダウンロード突破！日本と韓国両方のクリニックを予約できる',
    [Locales.EN_US]:
      'The No.1 beauty medical app in Korea. Compare prices, read real reviews and make counseling reservations all in one app. Experience GangnamUnni now.',
    [Locales.ZH_HANS_CN]: '韩国第一医美APP，真实评价、价格对比、预约咨询一站式服务。现在就体验GangnamUnni吧。',
    [Locales.ZH_HANT_TW]: '韓國第一醫美APP，真實評價、價格比較、預約諮詢一次搞定。現在就體驗GangnamUnni吧。',
  }

  const shortenDescription = serviceOffer.description
    ? serviceOffer.description.replace(/\n/g, '').slice(0, 100)
    : descriptionMap[locale]

  return {
    title: `${serviceOffer.title} | ${serviceOffer.hospital.name}`,
    description: shortenDescription,
    ogDescription: shortenDescription || '',
  }
}
